import { DatePart, DateUtil, LogManager, ModelRow, StringUtil } from "@mcleod/core";

const log = LogManager.getLogger("lme.dispatch.PlanningUtil");

export interface IncludeExcludeList {
    isExclude: boolean;
    dataField: string;
    values: string[];
}

type Prefix = "orig" | "dest";
type ibFilterType = "Z" | "S" | "C" | "L";

abstract class PlanningProfileFilter {
    protected filterRow: ModelRow;
    protected orderStartDate: Date;
    protected orderDate: Date;
    protected tractorDate: Date;

    abstract buildIncludeExcludeLists(): void;
    abstract dataPasses(data: ModelRow): boolean;

    constructor(filterRow: ModelRow) {
        this.filterRow = filterRow;
        this.buildIncludeExcludeLists();
        log.debug("Created filter", this);
    }

    protected addIncludeExcludeList(
            includeList:IncludeExcludeList[],
            includeExcludeListFieldName: string, //keyof RowBrokeragePlanningProfileFields | keyof RowOrderPlanningProfileFields
            dataFieldName?: string,
            includeExcludeFlagFieldName?: string) {
        includeList.push({
            isExclude: this.filterRow.getBoolean(includeExcludeFlagFieldName || includeExcludeListFieldName + "_exclude", false),
            values: this.tokenizeField(includeExcludeListFieldName, false, ","),
            dataField: dataFieldName || includeExcludeListFieldName
        });
    }

    protected tokenizeField(field: string, useZoneParsing: boolean, delimiter: string = ","): string[] {
        if (this.filterRow.isNull(field))
            return null;
        const result = this.filterRow.get(field).split(delimiter);
        for (let i = 0; i < result.length; i++)
            result[i] = result[i].trim();
        return result;
    }

    protected findItem(itemList: string[], item: string): boolean {
        if (itemList == null || itemList.length === 0 || "*" === itemList[0])
            return true;
        if (StringUtil.isEmptyString(item))
            return false;
        return itemList.indexOf(item) >= 0;
    }

    protected movementTypeMatches(data: ModelRow): boolean {
        const moveTypeList = this.tokenizeField("movement_type", false, "|");
        switch (data.get("movement_type")) {
            case "TKLD":
                if (!this.findItem(moveTypeList, "T"))
                    return false;
                break;
            case "PDDL":
                if (!this.findItem(moveTypeList, "P"))
                    return false;
                break;
            case "LINE":
                if (!this.findItem(moveTypeList, "L"))
                    return false;
                break;
        }
        return true;
    }

    protected availInboundOutboundMatches(data: ModelRow): boolean {
        const obType = this.filterRow.get("order_ob_type");
        let outbound = false;
        if ("Y" === obType) {
            if(this.checkZones(data, "order_ob", "origin"))
                outbound = true;
        } else {
            outbound = this.findItem(this.getIbObList("order_ob"), this.getFilterValue(data, false, false))
        }

        const ibType = this.filterRow.get("order_ib_type");
        let inbound = false;
        if ("Y" === ibType) {
            if(this.checkZones(data, "order_ib", "destination"))
                inbound = true;
        } else {
            inbound = this.findItem(this.getIbObList("order_ib"), this.getFilterValue(data, true, false))
        }

        if (outbound && inbound) {
            const orderDate = DateUtil.justDate(this.getOrderDate());
            const startDay = DateUtil.justDate(this.getStartDate());
            if (!this.filterRow.isNull("order_ib_zones") && "*" !== this.filterRow.get("order_ib_zones")
                && (this.filterRow.isNull("order_ob_zones") || "*" === this.filterRow.get("order_ob_zones")))
            {
                const destinationEarly = DateUtil.justDate(data.getDate("destination_early"));

                if (destinationEarly != null) {
                    if ((destinationEarly > orderDate && destinationEarly < startDay) || destinationEarly>orderDate) {
                        inbound = false;
                    }
                } else {
                    inbound = false;
                }
            } else {
                const originEarly = DateUtil.justDate(data.getDate("origin_early"));
                if (originEarly != null) {
                    if ((startDay != null && originEarly < startDay) || originEarly > orderDate) {
                        outbound = false;
                    }
                } else {
                    outbound = false;
                }
            }
        }

        if (this.filterRow.getBoolean("order_ib_zones_exclude", false)) {
            inbound = !inbound;
        }

        if (this.filterRow.getBoolean("order_ob_zones_exclude", false)) {
            outbound = !outbound;
        }

        return outbound && inbound;
    }

    protected equipInboundOutboundMatches(data: ModelRow): boolean {
        const outbound = this.listMatches(this.filterRow.getBoolean("tractor_ob_zones_exclude", false), this.getIbObList("tractor_ob"),
            this.getFilterValue(data, false, true));
        const inbound = this.listMatches(this.filterRow.getBoolean("tractor_ib_zones_exclude", false), this.getIbObList("tractor_ib"),
            this.getFilterValue(data, true, true));

        return inbound && outbound;
    }

    protected listMatches(exclude:boolean, matchList:string[], value:string): boolean {
        const listMatches = this.findItem(matchList, value);
        return (exclude && !listMatches) || (!exclude && listMatches);
    }

    protected  checkZones(data: ModelRow, filterPrefix:"order_ob" | "order_ib", stopPrefix: "origin" | "destination") {
        if (!this.filterRow.isNull(`${filterPrefix}_zones`) && !data.isNull(`${stopPrefix}_city`) && !data.isNull(`${stopPrefix}_state`)) {
            const city = data.get(`${stopPrefix}_city`);
            const state = data.get(`${stopPrefix}_state`);
            const cityState = this.filterRow.get(`${filterPrefix}_zones`).split(", ");
            if (cityState[0].toLowerCase() === city && cityState[1].toLowerCase() === state) {
                return true;
            }
        }
        return false;
    }

    protected getIbObList(prefix: string) {
        return this.tokenizeField(`${prefix}_zones`, "Z" === this.filterRow.get(`${prefix}_type`));
    }

    protected getFilterValue(data: ModelRow, inBound: boolean, isEquip: boolean): string {
        let type: ibFilterType;
        let prefix:"origin"|"destination";
        if (inBound) {
            prefix = "destination";
            type = this.filterRow.get(isEquip ? "tractor_ib_type" : "order_ib_type");
            if (isEquip && this.filterRow.getBoolean("use_preassign_dest", false)) {
                if ("Z" === type && !data.isNull("fp_dest_zone"))
                    return data.get("fp_dest_zone")
                else if ("S" === type && !data.isNull("fp_dest_state"))
                    return data.get("fp_dest_state");
                else if ("C" === type && !data.isNull("fp_dest_zip"))
                    return data.get("fp_dest_zip");
                else if ("L" === type && !data.isNull("fp_dest_location_id"))
                    return data.get("fp_dest_location_id");
            }
        } else {
            prefix = "origin";
            type = this.filterRow.get(isEquip ? "tractor_ob_type" : "order_ob_type");
        }
        if ("Z" === type)
            return data.get(prefix + "_zone");
        else if ("S" === type)
            return data.get(prefix + "_state");
        else if ("C" === type)
            return data.get(prefix + "_zip");
        else if ("L" === type)
            return data.get(prefix + "_location_id");

        return null;
    }

    public getOrderDate(): Date {
        if (this.orderDate == null) {
            const daysOut = this.filterRow.getNumber("order_days", 0);
            if (this.getStartDate() != null) {
                this.orderDate = this.getStartDate();
            } else {
                this.orderDate = DateUtil.justDate(new Date());
            }
            this.orderDate.setDate(this.orderDate.getDate() + daysOut);
            this.orderDate = DateUtil.justDate(this.orderDate);
        }
        return this.orderDate;
    }

    public getTractorDate(): Date {
        if (this.tractorDate == null) {
            this.tractorDate = new Date();
            this.tractorDate = new Date(this.tractorDate.setDate(this.tractorDate.getDate() + this.filterRow.getNumber("tractor_days", 0)));
            this.tractorDate = DateUtil.setToEndOfDay(this.tractorDate);
        }
        return this.tractorDate;
    }

    public getStartDate(): Date {
        if (this.orderStartDate == null && !this.filterRow.isNull("start_day"))
            this.orderStartDate = DateUtil.justDate(this.filterRow.getDate("start_day"));
        else
            this.orderStartDate = DateUtil.parseDateWithKeywords(this.filterRow.get("start_day"), true, false);
        return this.orderStartDate;
    }

    getDate(daysOutField: string): Date {
        return DateUtil.dateAdd(DatePart.DAY, this.getStartDate(), parseInt(this.filterRow.get(daysOutField, 0)));
    }

    protected dateTimeMatches(data: ModelRow, daysOutField: string, inboundField: string, outboundField: string,
                              validateStartDate: boolean) {
        const orderDate = this.getDate(daysOutField);
        const startDate = this.getStartDate();
        if (!this.filterRow.isNull(inboundField) && "*" !== this.filterRow.get(inboundField)
            && (this.filterRow.isNull(outboundField) || "*" === this.filterRow.get(outboundField))) {
            if (data.isNull("destination_early")) {
                return false;
            }
            if (data.get("destination_early") > orderDate) {
                return false;
            }
            if (validateStartDate && startDate != null && data.getDate("destination_early") < startDate) {
                return false;
            }
        } else {
            if (data.isNull("origin_early")) {
                return false;
            }
            if (data.get("origin_early") > orderDate) {
                return false;
            }
            if (validateStartDate && startDate != null && data.getDate("origin_early") < startDate) {
                return false;
            }
        }
        return true;
    }

    protected earlierDate(date1: Date, date2: Date) {
        if (date1 == null) {
            return date2;
        } else if (date2 == null) {
            return date1;
        } else if (date1 < date2) {
            return date1;
        } else if (date2 < date1) {
            return date2;
        } else {
            return null;
        }
    }

    protected passesPTA(data: ModelRow) {
        let earliestDate:Date;
        if (data != null) {
            if (!data.isNull("driver1_id")) {
                earliestDate = this.earlierDate(null, data.getDate("driver1_pta"));
            }
            if (!data.isNull("driver2_id")) {
                earliestDate = this.earlierDate(earliestDate, data.getDate("driver2_pta"));
            }
            if (earliestDate == null && !data.isNull("destination_early")) {
                earliestDate = data.getDate("destination_early");
            }
        }
        return !(earliestDate == null || earliestDate > this.getTractorDate());

    }

    protected passesDriverActiveStatus(data: ModelRow): boolean {
        const driverActiveStatus = this.filterRow.get("driver_active_status");
        const isDriverNull = data.isNull("driver1_id");
        const isDriverActive = data.getBoolean("driver1_active");

        return !((driverActiveStatus === "A" && (isDriverNull || !isDriverActive)) ||
            (driverActiveStatus === "I" && (isDriverNull || isDriverActive)));
    }
}

export class OrderPlanningFilter extends PlanningProfileFilter {
    private availIncludeLists: IncludeExcludeList[];
    private equipIncludeLists: IncludeExcludeList[];

    buildIncludeExcludeLists() {
        // Available
        this.availIncludeLists = [];
        this.addIncludeExcludeList(this.availIncludeLists, "revenue_code");
        this.addIncludeExcludeList(this.availIncludeLists, "order_type");
        this.addIncludeExcludeList(this.availIncludeLists, "order_customers", "customer_id");
        this.addIncludeExcludeList(this.availIncludeLists, "order_trlr_type", "equipment_type");
        this.addIncludeExcludeList(this.availIncludeLists, "order_tractor_fleet_id", "fleet_id", "order_tractor_fleet_x");
        this.addIncludeExcludeList(this.availIncludeLists, "order_driver_manager", "driver_manager", "order_driver_manager_x");
        this.addIncludeExcludeList(this.availIncludeLists, "show_broker_moves", "brokerage");
        this.addIncludeExcludeList(this.availIncludeLists, "order_late_status", "origin_late_status");
        this.addIncludeExcludeList(this.availIncludeLists, "order_companies", "company_id");
        // Equipment
        this.equipIncludeLists = [];
        this.addIncludeExcludeList(this.equipIncludeLists, "tractor_fleet", "fleet_id");
        this.addIncludeExcludeList(this.equipIncludeLists, "tractor_customers", "customer_id");
        this.addIncludeExcludeList(this.equipIncludeLists, "tractor_trlr_type", "trailer_type");
        this.addIncludeExcludeList(this.equipIncludeLists, "driver_manager", "driver1_manager");
        this.addIncludeExcludeList(this.equipIncludeLists, "tractor_latestatus", "origin_late_status");
        this.addIncludeExcludeList(this.equipIncludeLists, "tractor_companies", "company_id");
    }

    dataPasses(data: ModelRow): boolean {
        log.debug("Checking data", data);
        const isAvailable = data._modelPath === "lme/dispatch/available-move";
        for (const list of isAvailable ? this.availIncludeLists : this.equipIncludeLists) {
            if (list.dataField === "brokerage") {
                if (!this.brokerageMatches(data, list.isExclude))
                    return false;
            } else {
                const listMatches = this.findItem(list.values, data.get(list.dataField));
                if ((list.isExclude && listMatches) || (!list.isExclude && !listMatches))
                    return false;
            }
        }
        if (isAvailable) {
            if (!data.isNull("bill_date") && !data.isNull("xferred_2_settle_date"))
                return false;
            if (!this.filterRow.getBoolean("preass_order", false) && !data.isNull("tractor"))
                return false;
            if (!this.filterRow.getBoolean("show_ltl_orders", false) && data.getBoolean("ltl"))
                return false;
            if (this.filterRow.getBoolean("show_ltl_orders", false) && data.getBoolean("ltl")
                && !this.filterRow.getBoolean("include_on_hold_manifests") && data.getBoolean("manifest_hold"))
                return false;
            if (this.filterRow.getBoolean("order_late_only", false) && !data.getBoolean("origin_eta_late"))
                return false;
            if (!this.filterRow.getBoolean("order_include_accepted", false) && data.getBoolean("xmit_accepted"))
                return false;
            if (!this.filterRow.getBoolean("include_reserved", false) && data.getBoolean("driver_choice_excluded"))
                return false;
            if (!this.distanceMatches(data))
                return false;
            if (!this.dateTimeMatches(data, "order_days", "order_ib_zones", "order_ob_zones", true))
                return false;
            if (!this.availInboundOutboundMatches(data))
                return false;
            if (!this.filterRow.getBoolean("preass_order", false) && !data.isNull("tractor"))
                return false;
            if (!this.filterRow.getBoolean("include_on_hold_orders", true) && data.getBoolean("hold", false))
                return false;
        } else {
            if (("P" === data.get("move_status") || "D" === data.get("move_status"))) {
                if(!this.filterRow.getBoolean("preass_tractor", false) && !data.isNull("tractor") && data.getNumber("tractor_preassign_count", 0) > 0)
                    return false;
                const teams = this.filterRow.get("show_teams", "B");
                if ((data.isNull("driver2_id") && "T" === teams) || (!data.isNull("driver2_id") && "S" === teams))
                    return false;
                if (!this.checkTractorLateOor(data))
                    return false;
                if(!this.tractorTypeMatches(data))
                    return false;
            }
            if (this.filterRow.getBoolean("use_preassign_dest", false) && !data.isNull("tractor")
                && !data.isNull("fp_dest_early")  && data.getDate("fp_dest_early") > this.getTractorDate())
                return false;
            if (!this.filterRow.getBoolean("avail_tractor", false) &&
                ("A" === data.get("move_status") || "C" === data.get("move_status")))
                return false;
            if (this.filterRow.getBoolean("local_tractor", false) && !data.isNull("tractor_is_local")
                && data.getBoolean("tractor_is_local"))
                return false;
            if (("A" === data.get("driver1_active") && !data.isNull("driver1") && !data.getBoolean("driver1_active", false))
                || ("I" === data.get("driver1_active") && !data.isNull("driver1") && data.getBoolean("driver1_active", false)))
                return false;
            if (!this.teamDriverMatches(data))
                return false;
            if (!this.dateTimeMatches(data, "tractor_days", "tractor_ib_zones", "tractor_ob_zones", false))
                return false;
            if (!this.equipInboundOutboundMatches(data))
                return false;
            if (!data.getBoolean("tractor_active", false))
                return false;
            if (!this.passesDriverActiveStatus(data))
                return false;
            if (!this.passesPTA(data))
                return false;
            if (!this.filterRow.getBoolean("include_on_hold_e_orders", true) && data.getBoolean("hold", false))
                return false;
        }
        if (!this.movementTypeMatches(data))
            return false;
        return true;
    }

    brokerageMatches(data: ModelRow, isExclude: boolean): boolean {
        let passesBrkFilter = true;
        switch (this.filterRow.get("show_broker_moves")) {
            case "C":
                if (!data.getBoolean("allows_brokerage"))
                    passesBrkFilter = false;
                break;
            case "Y":
                if (!data.getBoolean("brokerage"))
                    passesBrkFilter = false;
                break;
            case "N":
                if (data.getBoolean("brokerage"))
                    passesBrkFilter = false;
                break;
        }
        if (isExclude)
            passesBrkFilter = !passesBrkFilter;
        return passesBrkFilter;
    }

    distanceMatches(data: ModelRow): boolean {
        const exMiles = this.filterRow.get("exclude_miles", 0);
        if (exMiles > 0) {
            const dist = data.get("distance");
            if (dist != null && dist < exMiles)
                return false;
        }
        return true;
    }

    teamDriverMatches(data: ModelRow): boolean {
        const teams = this.filterRow.get("team_drivers", "B");
        return !(("T" === teams && data.isNull("driver2_id")) || ("S" === teams && !data.isNull("driver2_id")));
    }

    tractorTypeMatches(data: ModelRow): boolean {
        const tractorTypeList = this.tokenizeField("tractor_types",false, ",")
        return this.listMatches(this.filterRow.getBoolean("tractor_types_exclude", false), tractorTypeList,
            data.get("tractor_type"));
    }

    checkTractorLateOor(data: ModelRow): boolean {
        const tractorLateOor = this.filterRow.get("tractor_late_oor", "");
        const isLate = data.getBoolean("next_stop_eta_late", false);
        const isOor = data.getBoolean("next_stop_eta_oor", false);
        if ("L" === tractorLateOor && !isLate)
            return false;
        if ("O" === tractorLateOor && !isOor)
            return false;
        if ("E" === tractorLateOor)
            return isLate || isOor;
        return true;
    }
}
